<template>
  <div>
    <div class="rs-scrim" @click="$emit('close')"></div>
    <div class="rs-bottom-sheet">
      <!-- Sticky sheet header -->
      <div class="rs-sheet-header">
        <span class="rs-grabber mx-auto d-block"></span>
        <div class="d-flex align-items-center justify-content-between px-4 pt-2">
          <div>
            <p class="overline mb-0" style="color:#6B7886;font-size:10px;letter-spacing:0.8px">YOUR RESERVATION</p>
            <h3 class="sub-heading-1-1 mb-0" style="letter-spacing:-0.2px">{{ reservation.room }}</h3>
          </div>
          <button @click="$emit('close')" class="rs-close-btn" type="button">
            <i class="simple-icon-close" style="font-size:13px"></i>
          </button>
        </div>
      </div>

      <!-- Scrollable body -->
      <div class="rs-body">

        <!-- Room summary strip -->
        <div class="rs-room-strip d-flex align-items-center px-4 py-3">
          <div class="flex-grow-1 overflow-hidden">
            <p class="mb-0 rs-room-num-line">Room · {{ reservation.roomNumber }}</p>
            <p class="mb-0 font-bold text-white rs-room-name">{{ reservation.room }}</p>
            <p class="mb-0 text-white rs-room-meta">
              {{ reservation.nights }} nights · {{ reservation.guests }} guests
            </p>
          </div>
          <div class="rs-nights-badge flex-shrink-0 ml-2 d-flex flex-column align-items-center">
            <span class="rs-nights-num">{{ reservation.daysIn }}</span>
            <span class="rs-nights-lbl">of {{ reservation.nights }}</span>
          </div>
        </div>

        <!-- Check-in / Check-out row — same style as ConciergeReservationCard -->
        <div class="d-flex align-items-center p-3 rs-date-row">
          <div class="flex-grow-1">
            <p class="rs-meta-label mb-0">CHECK-IN</p>
            <p class="mb-0 body-3-1 font-lato">{{ reservation.checkIn }}</p>
          </div>
          <div class="rs-day-pill d-flex align-items-center text-green bg-green-alpha border-green flex-shrink-0">
            <span class="font-bold" style="font-size:11px">Day {{ reservation.daysIn }}/{{ reservation.nights }}</span>
          </div>
          <div class="flex-grow-1 text-right">
            <p class="rs-meta-label mb-0">CHECK-OUT</p>
            <p class="mb-0 body-3-1 font-lato">{{ reservation.checkOut }}</p>
          </div>
        </div>

        <!-- Stay details -->
        <div class="px-4 pt-2 pb-2">
          <p class="overline mb-1">Room</p>
          <h4 class="heading-6 mb-3" style="letter-spacing:-0.2px;color:#1C2B36">Stay details</h4>
          <div class="rs-card overflow-hidden">
            <div
              v-for="(d, i) in stayDetails"
              :key="d.label"
              :class="['d-flex align-items-center px-3 py-3', { 'border-top': i > 0 }]"
            >
              <div class="rs-icon-box mr-3 flex-shrink-0 d-flex align-items-center justify-content-center">
                <component :is="d.icon" :size="16" :stroke-width="2" />
              </div>
              <div class="flex-grow-1 overflow-hidden">
                <p class="rs-meta-label mb-0">{{ d.label }}</p>
                <p class="mb-0 body-2-1" style="line-height:1.3">{{ d.value }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Room Policy -->
        <div class="px-4 pt-3 pb-2">
          <p class="overline mb-1">Guidelines</p>
          <h4 class="heading-6 mb-3" style="letter-spacing:-0.2px;color:#1C2B36">Room policy</h4>
          <div class="rs-card rs-policy-body px-3 py-3 html-desc" v-html="roomPolicyHtml"></div>
        </div>

        <!-- Bill / Folio -->
        <div class="px-4 pt-3 pb-2">
          <p class="overline mb-1">Charges</p>
          <h4 class="heading-6 mb-3" style="letter-spacing:-0.2px;color:#1C2B36">Bill &amp; folio</h4>

          <!-- Loading state -->
          <div v-if="isLoadingBill" class="rs-card p-4 d-flex align-items-center justify-content-center" style="min-height:120px">
            <div class="rs-bill-spinner mr-3"></div>
            <span class="body-3" style="color:#6B7886">Loading bill…</span>
          </div>

          <!-- Error state -->
          <div v-else-if="billError" class="rs-card p-4 text-center">
            <p class="mb-2 body-3" style="color:#C04040">{{ billError }}</p>
            <button @click="fetchBill" class="rs-retry-btn body-3" type="button">Retry</button>
          </div>

          <!-- Bill data -->
          <div v-else>

            <!-- Summary card -->
            <div class="rs-bill-summary rs-card mb-3 px-3 py-3">
              <div class="d-flex justify-content-between align-items-center rs-bill-summary-row">
                <span class="rs-bill-summary-label">Total charges</span>
                <span class="rs-bill-summary-total">{{ formatCurrency(billTotal) }}</span>
              </div>
              <div
                v-for="payment in billPayments"
                :key="'sum-' + payment.id"
                class="d-flex justify-content-between align-items-center rs-bill-summary-row"
              >
                <span class="rs-bill-summary-label">Payment received</span>
                <span class="rs-bill-summary-paid">- {{ formatCurrency(payment.amount) }}</span>
              </div>
              <div class="rs-bill-summary-divider"></div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="rs-bill-balance-label">Balance due at check-out</span>
                <span class="rs-bill-balance-value">{{ formatCurrency(billBalance) }}</span>
              </div>
            </div>

            <!-- Tab bar -->
            <div class="rs-bill-tabs rs-card mb-3">
              <button
                class="rs-bill-tab"
                :class="{ 'rs-bill-tab--active': billTab === 'bill' }"
                @click="billTab = 'bill'"
                type="button"
              >
                Bill
                <span class="rs-bill-tab-count">{{ billCharges.length }}</span>
              </button>
              <button
                class="rs-bill-tab"
                :class="{ 'rs-bill-tab--active': billTab === 'payment' }"
                @click="billTab = 'payment'"
                type="button"
              >
                Payment
                <span class="rs-bill-tab-count" :class="{ 'rs-bill-tab-count--paid': billPayments.length }">{{ billPayments.length }}</span>
              </button>
            </div>

            <!-- Bill tab -->
            <div v-if="billTab === 'bill'" class="rs-card">
              <div
                v-for="(item, i) in billCharges"
                :key="item.id"
                class="d-flex align-items-center px-3 py-2"
                :class="{ 'border-top': i > 0 }"
              >
                <div class="flex-grow-1 overflow-hidden pr-2">
                  <p class="mb-0 body-2-1" style="line-height:1.3">{{ item.remark || item.description }}</p>
                  <p class="mb-0 rs-bill-date">{{ item.dates }}</p>
                </div>
                <span class="font-semibold flex-shrink-0" style="font-size:13px">{{ formatCurrency(item.amount) }}</span>
              </div>
            </div>

            <!-- Payment tab -->
            <div v-else class="rs-card">
              <div
                v-for="(payment, i) in billPayments"
                :key="payment.id"
                class="d-flex align-items-center px-3 py-2"
                :class="{ 'border-top': i > 0 }"
              >
                <div class="flex-grow-1 overflow-hidden pr-2">
                  <p class="mb-0 body-2-1" style="line-height:1.3">{{ payment.description || 'Payment' }}</p>
                  <p class="mb-0 rs-bill-date" v-if="payment.dates">{{ payment.dates }}</p>
                </div>
                <span class="rs-bill-payment-amount flex-shrink-0">- {{ formatCurrency(payment.amount) }}</span>
              </div>
              <div v-if="!billPayments.length" class="px-3 py-4 text-center">
                <p class="mb-0 body-3" style="color:#888EA0;font-style:italic">No payments recorded yet</p>
              </div>
            </div>

          </div>
        </div>

        <div style="height:100px"></div>
      </div><!-- /rs-body -->

      <!-- Sticky pay button -->
      <div class="rs-pay-footer" v-if="!isLoadingBill && billBalance > 0">
        <button @click="goToPayment" class="rs-pay-btn w-100 font-bold" type="button">
          Pay Balance &mdash; {{ formatCurrency(billBalance) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatCurrency } from '@/utils/index.js'
import { get } from '@/services/index.js'
import { mapGetters } from 'vuex'
import {
  Moon,
  Maximize2,
  Users,
  Clock,
} from 'lucide-vue'

export default {
  name: 'ConciergeReservationSheet',

  components: { Moon, Maximize2, Users, Clock },

  props: {
    reservation: { type: Object, required: true },
    roomType:    { type: Object, default: null },
    merchant:    { type: Object, default: null },
  },

  computed: {
    ...mapGetters(['getConciergeHomeAuth']),
    billCharges() {
      return this.billRawData.filter(function(item) { return item.type !== 'PAYMENT' })
    },
    billPayments() {
      return this.billRawData.filter(function(item) { return item.type === 'PAYMENT' })
    },
    billTotal() {
      return this.billCharges.reduce(function(sum, item) { return sum + item.amount }, 0)
    },
    billPaid() {
      return this.billPayments.reduce(function(sum, item) { return sum + item.amount }, 0)
    },
    billBalance() {
      return this.billTotal - this.billPaid
    },
    stayDetails() {
      var rt = this.roomType
      var merchant = this.merchant
      var details = []

      details.push({ icon: Moon, label: 'Bed type', value: 'King bed' })

      if (rt && rt.room_size && String(rt.room_size) !== '0') {
        details.push({ icon: Maximize2, label: 'Room size', value: rt.room_size + ' m²' })
      }

      if (rt) {
        var occupancyValue = ''
        if (rt.use_max_guest_by_pax) {
          var parts = []
          if (rt.max_guest_adult) parts.push(rt.max_guest_adult + ' adult' + (rt.max_guest_adult > 1 ? 's' : ''))
          if (rt.max_guest_children) parts.push(rt.max_guest_children + ' child' + (rt.max_guest_children > 1 ? 'ren' : ''))
          if (rt.max_guest_infant) parts.push(rt.max_guest_infant + ' infant' + (rt.max_guest_infant > 1 ? 's' : ''))
          occupancyValue = parts.join(', ')
        } else if (rt.max_guests) {
          occupancyValue = rt.max_guests + ' guests'
        }
        if (occupancyValue) {
          details.push({ icon: Users, label: 'Max occupancy', value: occupancyValue })
        }
      }

      var checkoutTime = merchant && merchant.check_out_time_end
      if (checkoutTime) {
        details.push({ icon: Clock, label: 'Check-out time', value: checkoutTime })
      }

      return details
    },

    roomPolicyHtml() {
      if (!this.roomType || !this.roomType.policy || !this.roomType.policy.length) return ''
      return this.roomType.policy.map(function(p) {
        return '<p><strong>' + p.title + '</strong></p>' + p.description
      }).join('<div style="margin-top:16px"></div>')
    },
  },

  data() {
    return {
      isLoadingBill: false,
      billRawData: [],
      billError: '',
      billTab: 'bill',
    }
  },

  mounted() {
    this.fetchBill()
  },

  methods: {
    formatCurrency: formatCurrency,
    goToPayment() {
      var query = { src: 'gp-pms', folio: this.reservation.folio }
      var token = this.getConciergeHomeAuth && this.getConciergeHomeAuth.token
      if (token) query.token = token
      this.$router.push({
        name: 'PaymentBill',
        params: { invoice_id: this.reservation.detailId },
        query: query,
      })
    },
    fetchBill() {
      var detailId = this.reservation && this.reservation.detailId
      var folio = this.reservation && this.reservation.folio
      var merchantId = this.merchant && this.merchant.id
      if (!detailId || !folio) return

      this.isLoadingBill = true
      this.billError = ''
      this.billRawData = []

      get('CONCIERGE', 'reservation/' + detailId + '/' + folio + '/guest-bill', {
        merchant_id: merchantId,
      }).then(function(response) {
        var res = response.data
        if (res.success) {
          this.billRawData = res.data
        } else {
          this.billError = res.message || 'Failed to load bill'
        }
        this.isLoadingBill = false
      }.bind(this)).catch(function() {
        this.billError = 'Failed to load bill. Please try again.'
        this.isLoadingBill = false
      }.bind(this))
    },
  },
}
</script>

<style scoped>
.rs-scrim {
  position: fixed;
  inset: 0;
  background: rgba(20, 15, 10, 0.55);
  backdrop-filter: blur(2px);
  z-index: 1055;
}

.rs-bottom-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1056;
  background: #F5F7FA;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.18);
  animation: rsSheetUp 0.25s ease-out;
}

@keyframes rsSheetUp {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}

/* Sticky header */
.rs-sheet-header {
  background: #F5F7FA;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  padding-top: 12px;
  padding-bottom: 12px;
  flex-shrink: 0;
  border-bottom: 1px solid #E8EEF3;
}

.rs-grabber {
  width: 40px;
  height: 4px;
  border-radius: 99px;
  background: #D0D8E0;
  display: block;
  margin-bottom: 10px;
}

.rs-close-btn {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: #E8EEF3;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1C2B36;
  flex-shrink: 0;
}

/* Scrollable area */
.rs-body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Room strip */
.rs-room-strip {
  background: linear-gradient(135deg, var(--merchant-primary-color, #2B5F7A) 0%, #1a3a4b 100%);
  position: relative;
}

.rs-room-strip::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.1), transparent 70%);
  pointer-events: none;
}

.rs-room-num-line {
  font-size: 10px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 3px;
}

.rs-room-name {
  font-size: 14.5px;
  line-height: 1.25;
  position: relative;
  z-index: 1;
}

.rs-room-meta {
  font-size: 11px;
  opacity: 0.8;
  position: relative;
  z-index: 1;
}

.rs-nights-badge {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 6px 10px;
  position: relative;
  z-index: 1;
}

.rs-nights-num {
  font-size: 18px;
  font-weight: 900;
  color: #fff;
  line-height: 1;
}

.rs-nights-lbl {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* Date row */
.rs-date-row {
  background: #fff;
  border-bottom: 1px solid #E8EEF3;
}

.rs-meta-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6B7886;
}

.rs-day-pill {
  border-radius: 8px;
  padding: 4px 8px;
  flex-shrink: 0;
  font-size: 10px;
}

/* Cards */
.rs-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E0E8EF;
  box-shadow: 0 1px 2px rgba(15, 30, 50, 0.04), 0 4px 14px rgba(15, 30, 50, 0.06);
}

/* Icon box */
.rs-icon-box {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: #E6EFF4;
  color: var(--merchant-primary-color, #2B5F7A);
  flex-shrink: 0;
}

/* Policy HTML content */
.rs-policy-body >>> p {
  font-size: 13px;
  font-family: Nunito-Regular, sans-serif;
  color: #4E5A68;
  line-height: 1.6;
  margin-bottom: 8px;
}

.rs-policy-body >>> p:last-child {
  margin-bottom: 0;
}

/* Bill — tab bar */
.rs-bill-tabs {
  display: flex;
  overflow: hidden;
  padding: 4px;
  gap: 4px;
}

.rs-bill-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 12px;
  border: none;
  border-radius: 11px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #6B7886;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;
  -webkit-tap-highlight-color: transparent;
}

.rs-bill-tab--active {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
}

.rs-bill-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 99px;
  background: rgba(107, 120, 134, 0.15);
  font-size: 10px;
  font-weight: 700;
  padding: 0 4px;
}

.rs-bill-tab--active .rs-bill-tab-count {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}

.rs-bill-tab-count--paid {
  background: rgba(39, 174, 96, 0.15);
  color: #27AE60;
}

/* Bill — section overline */
.rs-section-overline {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #6B7886;
}

/* Bill — item date sub-label */
.rs-bill-date {
  font-size: 11px;
  color: #888EA0;
  margin-top: 1px;
}

/* Bill — payment amount (green) */
.rs-bill-payment-amount {
  font-size: 13px;
  font-weight: 600;
  color: #27AE60;
}

/* Bill — summary card rows */
.rs-bill-summary-row {
  margin-bottom: 6px;
}

.rs-bill-summary-label {
  font-size: 13px;
  color: #6B7886;
}

.rs-bill-summary-total {
  font-size: 13px;
  font-weight: 600;
  color: #1C2B36;
}

.rs-bill-summary-paid {
  font-size: 13px;
  font-weight: 600;
  color: #27AE60;
}

.rs-bill-summary-divider {
  height: 1px;
  background: #E0E8EF;
  margin: 10px 0;
}

.rs-bill-balance-label {
  font-size: 13px;
  font-weight: 600;
  color: #1C2B36;
}

.rs-bill-balance-value {
  font-size: 16px;
  font-weight: 700;
  color: #C04040;
}

/* Sticky pay footer */
.rs-pay-footer {
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #E8EEF3;
  flex-shrink: 0;
}

.rs-pay-btn {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 15px 20px;
  font-size: 15px;
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  transition: filter 0.12s;
}

.rs-pay-btn:active {
  filter: brightness(0.88);
}

.rs-pay-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.rs-bill-spinner {
  width: 20px;
  height: 20px;
  border: 2.5px solid #E0E8EF;
  border-top-color: var(--merchant-primary-color, #2B5F7A);
  border-radius: 50%;
  animation: rsBillSpin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes rsBillSpin {
  to { transform: rotate(360deg); }
}

.rs-retry-btn {
  background: none;
  border: 1px solid #C04040;
  color: #C04040;
  border-radius: 8px;
  padding: 6px 16px;
  cursor: pointer;
  font-family: inherit;
}
</style>



// WEBPACK FOOTER //
// src/components/v2/ConciergeReservationSheet.vue